// apps/web/src/components/message-bubble.tsx
import { Avatar, AvatarFallback } from '@viecxanh/chat-ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@viecxanh/chat-ui/tooltip'
import { cn } from '@viecxanh/chat-ui/utils'
import type { Message } from '@viecxanh/chat-shared'
import { formatTime } from '../lib/format'

interface MessageBubbleProps {
  message: Message
  isOwn?: boolean
}

export function MessageBubble({ message, isOwn = false }: MessageBubbleProps) {
  if (message.deleted_at) {
    return (
      <div className={cn('flex gap-2 mb-2', isOwn && 'flex-row-reverse')}>
        <span className="text-xs text-slate-400 italic py-1 px-2">Tin nhắn đã bị xóa</span>
      </div>
    )
  }

  const initials = message.sender.name.slice(0, 2).toUpperCase()

  return (
    <div className={cn('flex gap-2 mb-3 group', isOwn && 'flex-row-reverse')}>
      <Avatar className="h-7 w-7 flex-shrink-0 mt-1">
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>

      <div className={cn('flex flex-col max-w-[70%]', isOwn && 'items-end')}>
        {message.reply_to && (
          <div className="mb-1 rounded border-l-2 border-slate-300 bg-slate-100 px-2 py-1 text-xs text-slate-500">
            Trả lời tin nhắn
          </div>
        )}

        {message.type === 'text' && (
          <div
            className={cn(
              'rounded-2xl px-3 py-2 text-sm',
              isOwn
                ? 'bg-brand text-white rounded-br-sm'
                : 'bg-white text-slate-900 shadow-sm rounded-bl-sm',
            )}
          >
            {message.content}
          </div>
        )}

        {message.type === 'image' && message.attachments[0] && (
          <img
            src={message.attachments[0].url}
            alt={message.attachments[0].name}
            className="max-w-xs rounded-xl shadow-sm"
          />
        )}

        {message.type === 'file' && message.attachments[0] && (
          <a
            href={message.attachments[0].url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm shadow-sm hover:bg-slate-50"
          >
            <span className="text-xl">📄</span>
            <span className="truncate">{message.attachments[0].name}</span>
            <span className="text-xs text-slate-400">{formatBytes(message.attachments[0].size)}</span>
          </a>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="mt-0.5 text-[10px] text-slate-400 cursor-default">
              {formatTime(message.sent_at)}
              {isOwn && (
                <span className="ml-1">
                  {message.status === 'read' ? '✓✓' : message.status === 'delivered' ? '✓✓' : '✓'}
                </span>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs">{new Date(message.sent_at).toLocaleString('vi-VN')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
